import { SimulationCtx } from '@/contexts/Simulation/SimulationCtx';
import { AppDispatch, RootState } from '@/store';
import { createItem, deleteItem, update } from '@/store/storeSlice';
import { OperationResource, TreeItem } from '@/store/types';
import {
  Button,
  ButtonIcon,
  InputCounter,
  Label,
  Select,
  SelectOption,
  Stack,
  Typography,
} from '@data-products-and-ai/react-components';
import { produce } from 'immer';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export type TFormOperationsResources = {
  RESOURCE_ID: TreeItem;
  RESOURCE_NAME: TreeItem;
  RESOURCE_SHIFT_CALENDAR: TreeItem;
  RESOURCE_PRIORITY: TreeItem;
  OPERATION_ID: TreeItem;
  OPERATIONS_RESOURCES_ID: TreeItem;
  RESOURCE_TYPE_ID: TreeItem;
  MAX_QUANTITY_IN_BATCH: TreeItem;
  SETUP_TIME: TreeItem;
  PROCESS_TIME: TreeItem;
  PERSONNEL_TIME: TreeItem;
};

/**
 * FormOperationsResources Component
 * Manages the form for editing an operation resources details within a simulation scenario
 * Filters and sorts the operation resources based on the selected operation
 */
const FormOperationsResources = () => {
  const { simulationParams, setSimulationParams } = useContext(SimulationCtx);
  const dispatch: AppDispatch = useDispatch();
  const selectedScenario = useSelector((state: RootState) =>
    state.store.Simulation.scenarios.find((item) => item.is_selected),
  );

  const [removedResources, setRemovedResources] = useState<
    TFormOperationsResources[]
  >([]);
  const [addedResources, setAddedResources] = useState<
    TFormOperationsResources[]
  >([]);

  const filteredObjects: OperationResource[] = selectedScenario
    ? selectedScenario.data.operations_resources.allIds
        .map((id) => selectedScenario?.data.operations_resources.byId[id])
        .filter((obj) => obj.OPERATION_ID.value === simulationParams.formItem)
        .sort(
          (a, b) =>
            parseInt(a.RESOURCE_PRIORITY.value) -
            parseInt(b.RESOURCE_PRIORITY.value),
        )
    : [];

  const parseResource = (item: OperationResource): TFormOperationsResources => {
    const resource =
      selectedScenario?.data.resources.byId[item.RESOURCE_ID.value];

    if (!resource) {
      return {
        RESOURCE_ID: { value: '', originalValue: '' },
        RESOURCE_NAME: { value: '', originalValue: '' },
        RESOURCE_SHIFT_CALENDAR: { value: '', originalValue: '' },
        RESOURCE_PRIORITY: { value: '', originalValue: '' },
        OPERATION_ID: { value: '', originalValue: '' },
        OPERATIONS_RESOURCES_ID: { value: '', originalValue: '' },
        MAX_QUANTITY_IN_BATCH: { value: '', originalValue: '' },
        SETUP_TIME: { value: '', originalValue: '' },
        PROCESS_TIME: { value: '', originalValue: '' },
        PERSONNEL_TIME: { value: '', originalValue: '' },
        RESOURCE_TYPE_ID: { value: '', originalValue: '' },
      };
    }

    return {
      RESOURCE_ID: {
        value: resource.RESOURCE_ID.value,
        originalValue: resource.RESOURCE_ID.value,
      },
      RESOURCE_TYPE_ID: {
        value: item.RESOURCE_TYPE_ID.value,
        originalValue: item.RESOURCE_TYPE_ID.value,
      },
      RESOURCE_NAME: {
        value: resource.RESOURCE_NAME.value,
        originalValue: resource.RESOURCE_NAME.value,
      },
      RESOURCE_SHIFT_CALENDAR: {
        value:
          selectedScenario?.data.shift_calendar.byId[
            resource.RESOURCE_SHIFT_CALENDAR_ID.value
          ]?.RESOURCE_SHIFT_CALENDAR.value || '',
        originalValue:
          selectedScenario?.data.shift_calendar.byId[
            resource.RESOURCE_SHIFT_CALENDAR_ID.value
          ]?.RESOURCE_SHIFT_CALENDAR.value || '',
      },
      RESOURCE_PRIORITY: {
        value: item.RESOURCE_PRIORITY.value,
        originalValue: item.RESOURCE_PRIORITY.value,
      },
      OPERATION_ID: {
        value: item.OPERATION_ID.value || '',
        originalValue: item.OPERATION_ID.value || '',
      },
      OPERATIONS_RESOURCES_ID: {
        value: item.OPERATIONS_RESOURCES_ID.value || '',
        originalValue: item.OPERATIONS_RESOURCES_ID.value || '',
      },
      MAX_QUANTITY_IN_BATCH: {
        value: item.MAX_QUANTITY_IN_BATCH?.value || '',
        originalValue: item.MAX_QUANTITY_IN_BATCH?.value || '',
      },
      SETUP_TIME: {
        value: item.SETUP_TIME?.value || '',
        originalValue: item.SETUP_TIME?.value || '',
      },
      PROCESS_TIME: {
        value: item.PROCESS_TIME?.value || '',
        originalValue: item.PROCESS_TIME?.value || '',
      },
      PERSONNEL_TIME: {
        value: item.PERSONNEL_TIME?.value || '',
        originalValue: item.PERSONNEL_TIME?.value || '',
      },
    };
  };
  const initialAvailableResources = selectedScenario
    ? selectedScenario.data.operations_resources.allIds
        .map((id) => selectedScenario?.data.operations_resources.byId[id])
        .filter((obj) => obj.OPERATION_ID.value !== simulationParams.formItem)
        .sort(
          (a, b) =>
            parseInt(a.RESOURCE_PRIORITY.value) -
            parseInt(b.RESOURCE_PRIORITY.value),
        )
        .map((item) => {
          return parseResource(item);
        })
    : [];

  const [availableResources, setAvailableResources] = useState<
    TFormOperationsResources[]
  >(initialAvailableResources);

  const initalResources = filteredObjects.map((item: OperationResource) => {
    return parseResource(item);
  });

  useEffect(() => {
    setForm(initalResources);
  }, [simulationParams.drawerOpen]);

  const [form, setForm] = useState<TFormOperationsResources[]>([]);
  const [isEditingList, setIsEditingList] = useState(false);
  const [isEditingResource, setIsEditingResource] = useState(false);
  const [editingResource, setEditingResource] =
    useState<TFormOperationsResources | null>(null);

  const resourceType = editingResource
    ? selectedScenario?.data.resource_types.byId[
        editingResource?.RESOURCE_TYPE_ID.value
      ]?.RESOURCE_TYPE.value
    : '';

  const handleResources = () => {
    const operationResource = form.map(
      (item: TFormOperationsResources, idx: number) => {
        const resourceType =
          selectedScenario?.data.resource_types.byId[
            item.RESOURCE_TYPE_ID.value
          ]?.RESOURCE_TYPE.value;
        const isLastItem = idx === form.length - 1;
        const borderBottomStyle = isLastItem
          ? {}
          : { borderBottom: 'solid 1px #dadada' };

        if (
          !item.RESOURCE_NAME &&
          !item.RESOURCE_PRIORITY &&
          !resourceType &&
          !item.RESOURCE_SHIFT_CALENDAR
        )
          return;
        return (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 0.5fr 0.2fr',
              padding: 5,
              gap: 20,
              ...borderBottomStyle,
            }}
          >
            <div>{item.RESOURCE_NAME.value}</div>
            <div style={{wordBreak: 'break-all'}}>{resourceType}</div>
            <div>{item.RESOURCE_SHIFT_CALENDAR.value}</div>
            <div style={{ paddingRight: 10, textAlign: 'center' }}>
              {!isEditingList ? (
                item.RESOURCE_PRIORITY.value
              ) : (
                <Select
                  size="small"
                  defaultValue={item.RESOURCE_PRIORITY.value}
                  onChange={({ target }) =>
                    handleSelectPriority(target.value, idx)
                  }
                >
                  {form.map((_, idx) => {
                    const number = idx + 1;
                    return (
                      <SelectOption
                        key={number}
                        value={number.toString()}
                        label={number.toString()}
                        selected={
                          number.toString() === item.RESOURCE_PRIORITY.value
                        }
                      />
                    );
                  })}
                </Select>
              )}
            </div>
            {isEditingList ? (
              <div>
                <ButtonIcon
                  icon="IconClose"
                  onClick={() => handleRemoveResource(idx)}
                />
              </div>
            ) : (
              <div>
                <ButtonIcon
                  icon="IconEdit"
                  hoverType="color"
                  onClick={() => handleEditResource(idx)}
                />
              </div>
            )}
          </div>
        );
      },
    );

    const handleInputChange = (id: string, value: number | string) => {
      if (editingResource === null) return;
      setForm((prev) =>
        prev.map((item) => {
          if (
            item.RESOURCE_ID.value === editingResource.RESOURCE_ID.value &&
            item.OPERATION_ID.value === editingResource.OPERATION_ID.value
          ) {
            return {
              ...item,
              [id]: {
                value: value.toString(),
                originalValue:
                  item[id as keyof TFormOperationsResources].originalValue,
              },
            };
          }
          return item;
        }),
      );
      setEditingResource((prevResource) =>
        prevResource
          ? {
              ...prevResource,
              [id]: {
                value: value.toString(),
                originalValue:
                  prevResource[id as keyof TFormOperationsResources]
                    .originalValue,
              },
            }
          : null,
      );
    };

    const handleEditResource = (idx: number | null) => {
      setIsEditingResource(!isEditingResource);
      setEditingResource(form[idx || 0] || null);
    };

    const handleRemoveResource = (idx: number) => {
      if (
        initalResources.find(
          (elem) => elem.RESOURCE_ID.value === form[idx].RESOURCE_ID.value,
        )
      ) {
        setRemovedResources((prev) => {
          return [...prev, { ...form[idx] }];
        });
      }
      setForm((prev) => {
        const newForm = [...prev];
        newForm.splice(idx, 1);
        return newForm;
      });
      setAvailableResources((prev) => {
        const newAvailableResources = [...prev];
        const resource = form[idx];
        if (resource) {
          newAvailableResources.splice(0, 0, resource);
        }
        return newAvailableResources;
      });
    };

    const generateOperationsResourcesId = (): string => {
      const array = new Uint8Array(32); // 32 bytes = 64 hex characters
      window.crypto.getRandomValues(array);
      return Array.from(array, (byte) =>
        byte.toString(16).padStart(2, '0'),
      ).join('');
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      setIsEditingList(false);

      if (addedResources.length > 0) {
        addedResources.forEach((item) => {
          dispatch(
            createItem({
              key: 'OPERATIONS_RESOURCES_ID',
              area: 'operations_resources',
              content: item,
            }),
          );
        });
      }

      if (removedResources.length > 0) {
        removedResources.forEach((item) => {
          dispatch(
            deleteItem({
              id: item['OPERATIONS_RESOURCES_ID'].value,
              area: 'operations_resources',
            }),
          );
        });
      }

      setAddedResources([]);
      setRemovedResources([]);
      e.preventDefault();
      console.log('submit');
    };

    const handleCancel = () => {
      setIsEditingList(false);
      setSimulationParams(
        produce((draft) => {
          draft.formItem = undefined;
          draft.formArea = undefined;
          draft.drawerOpen = false;
        }),
      );

      setForm(initalResources);
    };

    const handleUpdateResource = (
      updatedResource: TFormOperationsResources | null,
    ) => {
      if (!updatedResource) return;
      dispatch(
        update({
          id: updatedResource.OPERATIONS_RESOURCES_ID.value,
          area: 'operations_resources',
          content: updatedResource,
        }),
      );
      handleEditResource(null);
    };

    const handleAddResource = (idx: number) => {
      const resource = availableResources[idx];
      if (!resource) return;
      const removedIdx = removedResources.findIndex(
        (item) => item.RESOURCE_ID.value === resource.RESOURCE_ID.value,
      );
      if (removedIdx !== -1) {
        removedResources.splice(removedIdx, 1);
      }
      setForm((prev) => {
        return [
          ...prev,
          {
            ...resource,
            OPERATION_ID: {
              value: simulationParams.formItem || '',
              originalValue: simulationParams.formItem || '',
            },
          },
        ];
      });
      setAvailableResources((prev) => {
        const newAvailableResources = [...prev];
        newAvailableResources.splice(idx, 1);
        return newAvailableResources;
      });
      if (
        !initalResources.find(
          (item) => item.RESOURCE_ID.value === resource.RESOURCE_ID.value,
        )
      ) {
        const operationsResourcesId = generateOperationsResourcesId();
        setAddedResources((prev) => [
          ...prev,
          {
            ...resource,
            OPERATION_ID: {
              value: simulationParams.formItem || '',
              originalValue: simulationParams.formItem || '',
            },
            OPERATIONS_RESOURCES_ID: {
              value: operationsResourcesId,
              originalValue: operationsResourcesId,
            },
            RESOURCE_TYPE_ID: resource.RESOURCE_TYPE_ID || {
              value: '',
              originalValue: '',
            },
            MAX_QUANTITY_IN_BATCH: resource.MAX_QUANTITY_IN_BATCH || {
              value: '',
              originalValue: '',
            },
            SETUP_TIME: resource.SETUP_TIME || { value: '', originalValue: '' },
            PROCESS_TIME: resource.PROCESS_TIME || {
              value: '',
              originalValue: '',
            },
            PERSONNEL_TIME: resource.PERSONNEL_TIME || {
              value: '',
              originalValue: '',
            },
          },
        ]);
      }
    };

    const handleSelectPriority = (e: string, idx: number) => {
      setForm((prev) => {
        const newForm = [...prev];
        newForm[idx].RESOURCE_PRIORITY.value = e;
        return newForm;
      });
    };

    return (
      <>
        {!isEditingResource ? (
          <form onSubmit={handleSubmit} style={{ height: '100%' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflowY: 'auto',
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 0.5fr 0.2fr',
                    padding: 5,
                    gap: 20,

                    borderBottom: 'solid 1px #dadada',
                    fontSize: 11,
                    fontWeight: 'bold',
                  }}
                >
                  <div>Resource</div>
                  <div>Type</div>
                  <div>Shift</div>
                  <div>Priority</div>
                </div>
                {operationResource}
                {isEditingList && (
                  <div>
                    <div style={{ marginBlock: 20, textAlign: 'center' }}>
                      <Typography tag="textsmall" color="#8a00e5">
                        Available Resources
                      </Typography>
                    </div>
                    {availableResources.map(
                      (resource: TFormOperationsResources, idx: number) => {
                        const isLastItem =
                          idx === availableResources.length - 1;
                        const borderBottomStyle = isLastItem
                          ? {}
                          : { borderBottom: 'solid 1px #dadada' };

                        return (
                          <div
                            key={idx}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 0fr',
                              padding: 5,
                              gap: 20,
                              ...borderBottomStyle,
                            }}
                          >
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 0.5fr',
                                gap: 20,
                                opacity: 0.5,
                              }}
                            >
                              <div>{resource.RESOURCE_NAME.value}</div>
                              <div>
                                {resource.RESOURCE_SHIFT_CALENDAR.value}
                              </div>
                              <div
                                style={{
                                  paddingRight: 10,
                                  textAlign: 'center',
                                }}
                              >
                                1
                              </div>
                            </div>
                            <div>
                              <ButtonIcon
                                icon="IconAdd"
                                onClick={() => handleAddResource(idx)}
                              />
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                )}
              </div>
              <div
                style={{
                  paddingTop: 10,
                  paddingBottom: 10,
                }}
              ></div>
              <div
                style={{
                  position: 'sticky',
                  bottom: 0,
                  marginTop: 30,
                  backgroundColor: 'white',
                  paddingBottom: 5,
                  paddingTop: 20,
                  borderTop: 'solid 1px #ededed',
                }}
              >
                <Stack direction="row" distribute="space-around">
                  <Button
                    buttonType="button"
                    type="primaryOutline"
                    width="200px"
                    onClick={() => handleCancel()}
                  >
                    Cancel
                  </Button>
                  {!isEditingList ? (
                    <Button
                      buttonType="button"
                      type="primaryNoShadow"
                      width="200px"
                      onClick={() => setIsEditingList(true)}
                    >
                      Edit
                    </Button>
                  ) : (
                    <Button
                      buttonType="submit"
                      type="primaryNoShadow"
                      width="200px"
                    >
                      Update
                    </Button>
                  )}
                </Stack>
              </div>
            </div>
          </form>
        ) : (
          <form
            style={{ height: '100%' }}
            onSubmit={(e) => {
              e.preventDefault();
              handleUpdateResource(editingResource);
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflowY: 'auto',
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 5,
                    gap: 20,
                    marginBottom: 30,

                    borderBottom: 'solid 1px #dadada',
                    fontSize: 11,
                    fontWeight: 'bold',
                  }}
                >
                  <Typography tag="h4" color="#8a00e5">Edit Process Resource</Typography>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography tag="textdefault_strong" >
                      {editingResource?.RESOURCE_NAME.value}
                    </Typography>
                    <Typography tag="textdefault">
                      {editingResource?.RESOURCE_SHIFT_CALENDAR.value}
                    </Typography>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    gap: 30,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      <Label>Setup Time</Label>
                      <InputCounter
                        defaultValue={parseInt(
                          editingResource?.SETUP_TIME.value || '0',
                        )}
                        min={0}
                        width={220}
                        id="SETUP_TIME"
                        onChange={(value) =>
                          handleInputChange('SETUP_TIME', value)
                        }
                        disabled={resourceType !== 'WORK_UNIT'}
                      />
                    </div>
                    <div>
                      <Label>Process Time</Label>
                      <InputCounter
                        id="PROCESS_TIME"
                        defaultValue={parseInt(
                          editingResource?.PROCESS_TIME.value || '0',
                        )}
                        min={0}
                        width={220}
                        onChange={(value) =>
                          handleInputChange('PROCESS_TIME', value)
                        }
                        disabled={resourceType !== 'WORK_UNIT'}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      <Label>Personnel Time</Label>
                      <InputCounter
                        id="PERSONNEL_TIME"
                        defaultValue={parseInt(
                          editingResource?.PERSONNEL_TIME.value || '0',
                        )}
                        min={0}
                        width={220}
                        onChange={(value) =>
                          handleInputChange('PERSONNEL_TIME', value)
                        }
                        disabled={resourceType !== 'WORK_UNIT'}
                      />
                    </div>
                    <div>
                      <Label>Resource Type</Label>
                      <Select
                        id="RESOURCE_TYPE_ID"
                        width={220}
                        defaultValue={editingResource?.RESOURCE_TYPE_ID.value}
                        onChange={({ target }) =>
                          handleInputChange('RESOURCE_TYPE_ID', target.value)
                        }
                      >
                        {selectedScenario?.data.resource_types.allIds.map(
                          (id) => {
                            const resourceType =
                              selectedScenario?.data.resource_types.byId[id];
                            return (
                              <SelectOption
                                key={resourceType.RESOURCE_TYPE_ID.value}
                                value={resourceType.RESOURCE_TYPE_ID.value}
                                label={resourceType.RESOURCE_TYPE.value}
                                selected={
                                  editingResource?.RESOURCE_TYPE_ID.value ===
                                  resourceType.RESOURCE_TYPE_ID.value
                                }
                              />
                            );
                          },
                        )}
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  paddingTop: 10,
                  paddingBottom: 10,
                }}
              ></div>
              <div
                style={{
                  position: 'sticky',
                  bottom: 0,
                  marginTop: 30,
                  backgroundColor: 'white',
                  paddingBottom: 5,
                  paddingTop: 20,
                  borderTop: 'solid 1px #ededed',
                }}
              >
                <Stack direction="row" distribute="space-around">
                  <Button
                    buttonType="button"
                    type="primaryOutline"
                    width="200px"
                    onClick={() => handleEditResource(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    buttonType="submit"
                    type="primaryNoShadow"
                    width="200px"
                  >
                    Update
                  </Button>
                </Stack>
              </div>
            </div>
          </form>
        )}
      </>
    );
  };

  if (!simulationParams.formItem) return 'Not found';

  return handleResources();
};

export default FormOperationsResources;
